import { html, repeat } from 'https://cdn.jsdelivr.net/gh/lit/dist@2.6.0/all/lit-all.min.js';
import { WishlistElement } from 'https://cdn.jsdelivr.net/npm/@appmate/wishlist@4.21.1/wishlist-element.js';
import { ProductFormController } from 'https://cdn.jsdelivr.net/npm/@appmate/wishlist@4.21.1/controllers.js';
import { Icon } from 'https://cdn.jsdelivr.net/npm/@appmate/wishlist@4.21.1/components/icon.js';
import 'https://cdn.jsdelivr.net/npm/@appmate/wishlist@4.21.1/components/button.js';
import 'https://cdn.jsdelivr.net/npm/@appmate/wishlist@4.21.1/components/option-select.js';
// import "https://cdn.jsdelivr.net/npm/@appmate/wishlist@4.21.1/components/option-swatches.js";

export class WishlistPage extends WishlistElement {
  getStateConfig() {
    return {
      wishlist: true,
    };
  }

  render() {
    if (!this.wishlist) {
      return;
    }

    return html`
      <section class="wk-page">
        <div class="wk-header">
          <h1 class="wk-title">${this.getTranslation('wishlist_page.title')}</h1>
          ${this.renderWishlistEmptyCallout()} ${this.renderLoginCallout()} ${this.renderControls()}
        </div>
        <div class="wk-body">${this.renderWishlistItems()}</div>
      </section>
    `;
  }

  renderControls() {
    if (!this.wishlist.items.length) {
      return;
    }

    return html`
      <div class="wk-controls">
        <wishlist-share data-wishlist-id="${this.wishlist.id}" layout="icon-and-text"></wishlist-share>
        <wishlist-buy-all data-wishlist-id="${this.wishlist.id}" layout="icon-and-text"></wishlist-buy-all>
      </div>
    `;
  }

  renderWishlistEmptyCallout() {
    if (this.wishlist.items.length) {
      return;
    }

    return html`
      <div class="wk-wishlist-empty-callout">
        <p>${this.getTranslation('wishlist_page.wishlist_empty_callout_html')}</p>
      </div>
    `;
  }

  renderLoginCallout() {
    if (this.app.customer || !this.wishlist.isMine || !this.wishlist.items.length) {
      return;
    }

    return html`
      <div class="wk-login-callout">
        <p>
          ${this.getTranslation('wishlist_page.login_callout_html', {
            login_url: this.app.routes.accountLoginUrl,
            register_url: this.app.routes.accountRegisterUrl,
          })}
        </p>
      </div>
    `;
  }

  renderWishlistItems() {
    if (!this.wishlist.items.length) {
      return;
    }

    const wishlistItems = this.wishlist.items.slice().reverse();

    return html`
      <div class="wk-grid">
        ${repeat(
          wishlistItems,
          (wishlistItem) => wishlistItem.id,
          (wishlistItem) => html`
            <wishlist-product-card
              data-wishlist-id=${this.wishlist.id}
              data-wishlist-item-id=${wishlistItem.id}
              .wishlistId=${this.wishlist.id}
              .isMine=${this.wishlist.isMine}
            ></wishlist-product-card>
          `
        )}
      </div>
    `;
  }
}

customElements.define('wishlist-page', WishlistPage);

export class WishlistProductCard extends WishlistElement {
  static get properties() {
    return {
      isMine: { type: Boolean },
      wishlistId: { type: String },
    };
  }

  constructor() {
    super();
    this.form = new ProductFormController(this);
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('wishlistItem')) {
      this.form.setProduct({
        product: this.wishlistItem.product,
        selectedVariantId: this.wishlistItem.variantId,
        autoSelect: false,
      });
    }
  }

  getStateConfig() {
    return {
      loading: 'lazy',
      wishlistItem: true,
    };
  }

  getEventConfig() {
    return {
      'change .wk-form': async (event) => {
        this.form.changeOption({
          input: event.target,
          autoSelect: false,
        });

        if (this.form.selectedVariant && this.isMine) {
          await this.app.updateWishlistItem({
            wishlistItemId: this.wishlistItem.id,
            changes: {
              variantId: this.form.selectedVariant.id,
            },
          });
        }
      },
      'submit .wk-form': async (event) => {
        event.preventDefault();

        await this.form.addToCart({
          wishlistId: this.wishlistId,
          wishlistItemId: this.wishlistItem.id,
        });
      },
    };
  }

  render() {
    if (!this.wishlistItem.product.id) {
      return html` <div class="wk-product-card">${this.renderLoadingState()}</div> `;
    }

    if (this.wishlistItem.product.hidden) {
      return html` <div class="wk-product-card">${this.renderUnavailableState()}</div> `;
    }

    const product = this.wishlistItem.product;
    const variant = this.form.selectedVariant;

    return html`
      <div class="wk-product-card">
        <a href=${this.getProductUrl(product, variant)} class="wk-image-link">
          <img
            class="wk-image"
            src=${this.getImageUrl(product, variant, {
              width: 1000,
              height: 1000,
            })}
          />
        </a>
        <div class="wk-meta">
          <span class="wk-vendor">${product.vendor}</span>
          <a class="wk-product-title" href=${this.getProductUrl(product, variant)}> ${product.title} </a>
          <div class="wk-price">
            ${this.renderCurrentPrice({ product, variant })} ${this.renderComparePrice({ product, variant })}
          </div>
          ${this.renderUnitPrice({ product, variant })}
        </div>
        ${this.renderProductForm({ product, variant })} ${this.renderRemoveButton()} ${this.renderWishlistButton()}
      </div>
    `;
  }

  renderCurrentPrice({ product, variant }) {
    if (product.tags.includes('Coming Soon')) return;

    if (variant) {
      const sale = variant.price < variant.compare_at_price;

      return html`
        <span class="wk-current-price ${sale ? 'wk-sale' : ''}"> ${this.renderMoney(variant.price)} </span>
      `;
    }

    if (product.price_min !== product.price_max) {
      return html`
        <span class="wk-current-price">
          ${this.getTranslation('wishlist_product.from_price_html', {
            price: this.formatMoney(product.price_min),
          })}
        </span>
      `;
    }

    return html` <span class="wk-current-price"> ${this.renderMoney(product.price_min)} </span> `;
  }

  renderComparePrice({ product, variant }) {
    if (product.tags.includes('Coming Soon')) return;

    if (variant && variant.price < variant.compare_at_price) {
      return html` <span class="wk-compare-price"> ${this.renderMoney(variant.compare_at_price)} </span> `;
    }
  }

  renderUnitPrice({ variant }) {
    if (!variant) {
      return;
    }

    const unitPrice = variant.unit_price_measurement;

    if (!unitPrice) {
      return;
    }

    const baseUnit = unitPrice.reference_value != 1 ? unitPrice.reference_value : unitPrice.reference_unit;

    return html`
      <div class="wk-unit-price">
        <span class="wk-unit-price-money">${this.renderMoney(variant.unit_price)}</span>
        <span class="wk-unit-price-separator"> / </span>
        <span class="wk-unit-price-unit">${baseUnit}</span>
      </div>
    `;
  }

  renderProductForm({ product, variant }) {
    if (product.tags.includes('Coming Soon')) {
      return html`
        <div class="wk-form">
          <a class="wk-submit-button" href=${product.url}>
            <span class="wk-submit-label">${this.getTranslation('wishlist_product.coming_soon')}</span>
          </a>
        </div>
      `;
    }

    const getSubmitText = () => {
      if (!variant && this.form.hasSelection) {
        return this.getTranslation('wishlist_product.unavailable');
      } else if (!variant) {
        return this.getTranslation('wishlist_product.select_option', {
          name: this.form.optionsWithValues.find((option) => !option.selectedValue).name,
        });
      }

      if (!variant.available) {
        return this.getTranslation('wishlist_product.sold_out');
      }

      return this.getTranslation('wishlist_product.add_to_cart');
    };

    return html`
      <form
        class="wk-form"
        method="post"
        action=${this.app.routes.cartAddUrl}
        data-wishlist-id=${this.wishlistId}
        data-wishlist-item-id=${this.wishlistItem.id}
      >
        <input name="id" value=${this.form.selectedVariant ? this.form.selectedVariant.id : ''} type="hidden" />
        <div class="wk-variants">${this.renderProductOptions()}</div>
        <div class="wk-quantity">
          <label class="wk-quantity-label"> ${this.getTranslation('wishlist_product.quantity')} </label>
          <input class="wk-quantity-input" type="number" name="quantity" value="1" min="1" />
        </div>
        <button
          type="submit"
          class="wk-submit-button"
          data-wishlist-item-id=${this.wishlistItem.id}
          ?disabled=${!variant || !variant.available}
        >
          <span class="wk-submit-label">${getSubmitText()}</span>
          <wk-icon icon="spinner" class="wk-submit-spinner"></wk-icon>
        </button>
      </form>
    `;
  }

  renderProductOptions() {
    if (this.form.hasOnlyDefaultVariant) {
      return;
    }

    // return html`
    //   ${this.form.optionsWithValues.map((option) => {
    //     return html`
    //       <wk-option-swatches
    //         id=${`${this.wishlistItem.id}-${option.name}`}
    //         .option=${option}
    //       ></wk-option-swatches>
    //     `;
    //   })}
    // `;

    return html`
      ${this.form.optionsWithValues.map(
        (option) =>
          html`
            <wk-option-select
              id=${`${this.wishlistItem.id}-${option.name}`}
              .option=${option}
              .defaultText=${this.getTranslation('wishlist_product.select_option', option)}
            ></wk-option-select>
          `
      )}
    `;
  }

  renderLoadingState() {
    return html`
      <div class="wk-image">
          <wk-icon icon="spinner" class="wk-loading-spinner"></wk-icon>
        </div>  
      </div>
    `;
  }

  renderUnavailableState() {
    return html`
      <div class="wk-image-link">
        <img class="wk-image" src=${this.getImageUrl(null, null, { width: 1000, height: 1000 })} />
      </div>
      <div class="wk-meta">
        <span class="wk-vendor">&nbsp;</span>
        <span class="wk-product-title"> ${this.getTranslation('wishlist_page.product_removed_html')} </span>
      </div>
      ${this.renderRemoveButton()}
    `;
  }

  renderRemoveButton() {
    if (!this.isMine) {
      return;
    }

    const floatSettings = {
      reference: this,
      position: {
        placement: 'top-end',
        inset: true,
      },
    };

    return html` <remove-button data-wishlist-item-id=${this.wishlistItem.id} layout="text-link"></remove-button> `;
  }

  renderWishlistButton() {
    if (this.isMine) {
      return;
    }

    return html`
      <wishlist-button
        data-product-id=${this.form.productId}
        data-variant-id=${this.form.selectedVariantId}
        layout="icon-only"
        floating='{"reference": "#{{ container_id }} .wk-image", "position": {"placement": "top-end", "inset": true}}'
      ></wishlist-button>
    `;
  }
}

customElements.define('wishlist-product-card', WishlistProductCard);

export class WishlistButton extends WishlistElement {
  constructor() {
    super();
    this.app.events.subscribe('wk:wishlist:add:success', this.onAdded.bind(this));
    this.app.events.subscribe('wk:wishlist:remove:success', this.onAdded.bind(this));
    // this.addEventListener('wk:wishlist:add:success', this.onAdded.bind(this));
  }
  onAdded() {
    this?.classList.remove('loading');
  }
  static get properties() {
    return {
      layout: { type: String, reflect: true },
      alignment: { type: String, reflect: true },
      outline: { type: Boolean, reflect: true },
      fullWidth: { type: Boolean, reflect: true },
      floating: { type: Object, reflect: true },
    };
  }

  getStateConfig() {
    return {
      productInfo: true,
    };
  }

  getEventConfig() {
    return {
      'click wk-button': this.handleClick,
    };
  }

  handleClick() {
    this?.classList.add('loading');
    if (this.productInfo.inWishlist) {
      return this.app.removeWishlistItem(this.productInfo);
    } else {
      return this.app.addWishlistItem(this.productInfo);
    }
  }

  render() {
    if (!this.productInfo) {
      return;
    }

    const inWishlist = this.productInfo.inWishlist;
    const text = this.getTranslation(
      inWishlist ? 'wishlist_buttons.product_in_wishlist' : 'wishlist_buttons.add_product'
    );
    const hint = this.getTranslation(inWishlist ? 'wishlist_buttons.remove_product' : 'wishlist_buttons.add_product');

    return html`
      <wk-button
        .text=${text}
        .hint=${hint}
        .selected=${inWishlist}
        .layout=${this.layout}
        .alignment=${this.alignment}
        .outline=${this.outline}
        .fullWidth=${this.fullWidth}
        .floating=${this.floating}
        .icon=${inWishlist ? 'wishlist' : 'wishlist2'}
      ></wk-button>
    `;
  }
}

customElements.define('wishlist-button', WishlistButton);

export class RemoveButton extends WishlistElement {
  static get properties() {
    return {
      layout: { type: String },
      alignment: { type: String },
      outline: { type: Boolean },
      fullWidth: { type: Boolean },
      floating: { type: Object },
    };
  }

  getEventConfig() {
    return {
      'click .wk-button': this.handleClick,
    };
  }

  handleClick() {
    return this.app.removeWishlistItem({
      wishlistItemId: this.dataset.wishlistItemId,
    });
  }

  render() {
    const text = this.getTranslation('wishlist_page.remove_product');
    const hint = this.getTranslation('wishlist_page.remove_product');

    return html`
      <wk-button
        .text=${text}
        .hint=${hint}
        .layout=${this.layout}
        .alignment=${this.alignment}
        .outline=${this.outline}
        .fullWidth=${this.fullWidth}
        .floating=${this.floating}
        .icon=${'remove'}
      ></wk-button>
    `;
  }
}

customElements.define('remove-button', RemoveButton);

export class WishlistLink extends WishlistElement {
  static get properties() {
    return {
      layout: { type: String },
      alignment: { type: String },
      outline: { type: Boolean },
      fullWidth: { type: Boolean },
      floating: { type: Object },
      badgeLayout: { type: String },
      badgeParentheses: { type: Boolean },
      badgeHidden: { type: Boolean },
      badgeHiddenIfEmpty: { type: Boolean },
      badgeFloating: { type: Object },
    };
  }

  getStateConfig() {
    return {
      wishlist: true,
    };
  }

  getWishlistUrl() {
    if (this.app.settings.loginRequired) {
      return this.app.routes.accountLoginUrl;
    }
    return this.app.routes.wishlistUrl;
  }

  render() {
    const numItems = this.wishlist ? this.wishlist.numItems : 0;
    const wishlistUrl = this.getWishlistUrl();
    const text = this.getTranslation('wishlist_buttons.wishlist');
    const hint = this.getTranslation('wishlist_buttons.view_wishlist');

    return html`
      <wk-button
        .href=${wishlistUrl}
        .text=${text}
        .hint=${hint}
        .selected=${numItems > 0}
        .layout=${this.layout}
        .alignment=${this.alignment}
        .outline=${this.outline}
        .fullWidth=${this.fullWidth}
        .floating=${this.floating}
        .icon=${'wishlist'}
        .badgeText=${numItems}
        .badgeLayout=${this.badgeLayout}
        .badgeParentheses=${this.badgeParentheses}
        .badgeHidden=${this.badgeHidden}
        .badgeHiddenIfEmpty=${this.badgeHiddenIfEmpty}
        .badgeFloating=${this.badgeFloating}
      ></wk-button>
    `;
  }
}

customElements.define('wishlist-link', WishlistLink);

export class WishlistShare extends WishlistElement {
  static get properties() {
    return {
      layout: { type: String },
      alignment: { type: String },
      floating: { type: Object },
      linkCopied: { type: Boolean, state: true },
    };
  }

  getStateConfig() {
    return {
      wishlist: true,
    };
  }

  getEventConfig() {
    return {
      'click wk-button': this.handleClick,
    };
  }

  async handleClick() {
    const { clipboard } = await this.app.shareWishlist({
      wishlistId: this.wishlist.publicId,
      title: this.getTranslation('wishlist_share.share_title'),
      text: this.getTranslation('wishlist_share.share_message'),
    });

    if (clipboard) {
      this.linkCopied = true;
      await new Promise((resolve) => setTimeout(resolve, 2000));
      this.linkCopied = false;
    }
  }

  render() {
    const text = this.getTranslation(this.linkCopied ? 'wishlist_share.link_copied' : 'wishlist_share.button_label');

    return html`
      <wk-button
        .text=${text}
        .hint=${text}
        .layout=${this.layout}
        .alignment=${this.alignment}
        .floating=${this.floating}
        .icon="${'share'}"
      ></wk-button>
    `;
  }
}

customElements.define('wishlist-share', WishlistShare);

export class WishlistBuyAll extends WishlistElement {
  static get properties() {
    return {
      layout: { type: String },
      alignment: { type: String },
      floating: { type: Object },
    };
  }

  getStateConfig() {
    return {
      wishlist: true,
    };
  }

  getEventConfig() {
    return {
      'click wk-button': this.handleClick,
    };
  }

  async handleClick() {
    await this.app.addAllToCart({
      wishlistId: this.wishlist.id,
    });
  }

  render() {
    const text = this.getTranslation('wishlist_page.add_all_to_cart');

    return html`
      <wk-button
        .text=${text}
        .hint=${text}
        .layout=${this.layout}
        .alignment=${this.alignment}
        .floating=${this.floating}
        .icon="${'buy'}"
      ></wk-button>
    `;
  }
}

customElements.define('wishlist-buy-all', WishlistBuyAll);

export class WishlistSaveForLater extends WishlistElement {
  static get properties() {
    return {
      layout: { type: String },
      alignment: { type: String },
      outline: { type: Boolean },
      fullWidth: { type: Boolean },
      floating: { type: Object },
    };
  }

  getStateConfig() {
    return {
      productInfo: true,
    };
  }

  getEventConfig() {
    return {
      'click wk-button': this.handleClick,
    };
  }

  async handleClick() {
    if (!this.productInfo.inWishlist) {
      await this.app
        .addWishlistItem(this.productInfo)
        .then(() => {
          this.app.events.publish({
            name: 'wk:cart:save-for-later:success',
            data: { trigger: this },
          });
        })
        .catch(() => {
          this.app.events.publish({
            name: 'wk:cart:save-for-later:error',
            data: { trigger: this },
          });
        });
    }
  }

  render() {
    const inWishlist = this.productInfo && this.productInfo.inWishlist;

    const text = this.getTranslation(
      inWishlist ? 'wishlist_buttons.product_in_wishlist' : 'wishlist_buttons.save_for_later'
    );

    const hint = this.getTranslation(
      inWishlist ? 'wishlist_buttons.product_in_wishlist' : 'wishlist_buttons.save_for_later'
    );

    return html`
      <wk-button
        .text=${text}
        .hint=${hint}
        .selected=${inWishlist}
        .disabled=${inWishlist}
        .layout=${this.layout}
        .alignment=${this.alignment}
        .outline=${this.outline}
        .fullWidth=${this.fullWidth}
        .floating=${this.floating}
        .icon=${'wishlist'}
      ></wk-button>
    `;
  }
}

customElements.define('wishlist-save-for-later', WishlistSaveForLater);

Icon.addIcons({
  wishlist: `
    <svg viewBox="0 0 500 500">
      <g>
    	<path d="M418.35,252.91c0.48-9.79-0.66-19.69,0.27-29.42c1.39-14.68-4.38-19.33-19-19.11c-49.7,0.74-99.43,0.28-149.14,0.28
    		c-50.37,0.05-100.74-0.2-151.11,0.4c-5.99,0.07-16.81,4.39-17.08,7.46c-1.64,18.81-1.09,37.83-0.67,56.76
    		c0.05,2.34,3.81,4.52,5.66,6.92c4.6,5.94,12.26,11.6,13.01,18.03c2.02,17.28,3.26,35.21,0.85,52.33
    		c-6.06,43.03,15.56,73.85,58.57,78.18c14.16,1.43,21.94,5.03,20.11,21.15c-2.3,20.26,8.3,34.42,27.66,36.02
    		c28.3,2.35,57.08,2.27,85.4-0.01c18.43-1.49,29.01-15.17,27.13-34.52c-1.58-16.29,4.91-21.17,20.6-22.71
    		c42.81-4.2,64.23-35.79,58.12-78.51c-2.62-18.32,0.24-37.48,1.33-56.22c0.24-4.13,3.07-11.3,5.42-11.61
    		C422.96,276.05,417.85,263.01,418.35,252.91z M177.6,389.81c-22.43-0.42-42.47-19.9-42.08-40.89
    		c0.38-20.02,16.91-36.08,36.74-35.68c25.08,0.51,55.72,23.2,55.91,41.41C228.34,371.43,201.27,390.25,177.6,389.81z M250.85,417.12
    		c-9.59,0.09-27.22,14.51-25.07-7.13c1.51-15.17,15.79-29.08,24.37-43.55c8.5,14.55,22.31,28.47,24.16,43.82
    		C276.58,429.25,260.34,419.47,250.85,417.12z M321.18,389.74c-23.72,0.31-50.58-19.22-49.61-36.08
    		c1.04-18.06,32.71-40.36,57.09-40.18c20.19,0.15,35.96,16.24,35.73,36.47C364.15,371.01,344.14,389.43,321.18,389.74z"/>
    	<path d="M179.22,187.2c0-49.52,0.65-100.65-0.41-148.9c-0.31-14.13-11.37-22.02-25.56-21.95c-14.25,0.06-25.01,8.15-25.3,22.29
    		c-1,48.21-0.38,99.3-0.38,148.56C145.14,187.2,160.62,187.2,179.22,187.2z"/>
    	<path d="M372.45,186.59c0-48.09,0.29-97.33-0.16-143.72c-0.15-16.02-9.11-26.14-25.49-26.34c-15.06-0.19-25.76,8.32-26.02,23.62
    		c-0.81,47.58-0.3,98.03-0.3,146.45C339.6,186.59,355.51,186.59,372.45,186.59z"/>
      </g>
    </svg>
  `,
  wishlist2: `
    <svg viewBox="0 0 337.646496 467.3">
        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <g id="Plug-Skull-Icon-State-1" transform="translate(0.000000, 0.000000)" fill="#000000" fill-rule="nonzero">      
                <path d="M319.388163,195.03 C325.888163,195.03 328.328163,196.21 329.238163,197.21 C330.468163,198.56 330.908163,201.86 330.468163,206.49 C329.908163,212.35 330.048163,218.16 330.178163,223.78 C330.278163,228.1 330.378163,232.18 330.178163,236.23 C330.058163,238.6 330.198163,240.95 330.328163,243.22 C330.528163,246.63 330.728163,250.15 329.858163,252.08 C329.608163,252.64 328.838163,254.35 323.368163,255.07 C314.718163,256.21 312.068163,269.89 311.868163,273.22 C311.608163,277.71 311.238163,282.35 310.878163,286.83 C309.718163,301.27 308.508163,316.21 310.608163,330.83 C313.508163,351.1 309.838163,368.44 299.998163,380.98 C290.898163,392.57 276.628163,399.63 258.738163,401.38 C244.128163,402.81 229.508163,407.54 231.858163,431.73 C232.628163,439.71 230.938163,446.44 226.968163,451.18 C223.368163,455.48 217.888163,458.04 211.128163,458.59 C196.978163,459.73 182.668163,460.31 168.588163,460.31 C154.558163,460.31 140.528163,459.74 126.868163,458.6 C119.728163,458.01 114.008163,455.33 110.338163,450.86 C106.268163,445.91 104.628163,438.81 105.588163,430.34 C106.568163,421.71 105.178163,415.21 101.358163,410.46 C96.1081627,403.96 87.6281627,402.28 79.2281627,401.43 C60.9681627,399.59 46.4981627,392.47 37.3781627,380.85 C27.6881627,368.49 24.0581627,351.32 26.8881627,331.19 C29.5281627,312.42 27.9481627,293.3 26.0581627,277.08 C25.2681627,270.29 20.3181627,265.05 15.9481627,260.42 C14.3381627,258.72 12.8281627,257.11 11.6381627,255.58 C10.6181627,254.25 9.42816269,253.17 8.38816269,252.21 C8.09816269,251.94 7.71816269,251.6 7.40816269,251.29 L7.37816269,249.77 C6.99816269,232.99 6.59816269,215.66 7.91816269,198.88 C10.2181627,197.54 15.2581627,195.75 18.2781627,195.71 C48.2281627,195.35 77.7281627,195.31 105.158163,195.31 C113.088163,195.31 121.028163,195.31 128.958163,195.32 C136.888163,195.32 144.828163,195.33 152.768163,195.33 C158.278163,195.33 163.798163,195.33 169.308163,195.32 C183.038163,195.32 197.008163,195.36 210.508163,195.39 C224.018163,195.42 237.998163,195.46 251.748163,195.46 C277.928163,195.46 299.158163,195.33 318.558163,195.04 C318.828163,195.03 319.108163,195.03 319.388163,195.03 M97.2381627,380.47 C113.408163,380.47 131.428163,372.83 143.138163,361 C150.318163,353.75 154.068163,345.88 153.988163,338.23 C153.878163,327.34 146.058163,315.35 132.538163,305.33 C119.848163,295.93 104.398163,290.16 91.2181627,289.89 C90.9281627,289.88 90.6381627,289.88 90.3481627,289.88 C67.0781627,289.88 47.7781627,308.97 47.3381627,332.44 C47.1181627,344.37 52.4381627,356.52 61.9381627,365.75 C71.3181627,374.86 83.8381627,380.23 96.2781627,380.46 C96.5981627,380.47 96.9181627,380.47 97.2381627,380.47 M239.438163,380.39 C239.658163,380.39 239.878163,380.39 240.088163,380.39 C252.918163,380.22 265.268163,375.26 274.858163,366.43 C284.468163,357.58 290.068163,345.64 290.198163,333.68 C290.328163,322.01 285.988163,311.08 277.968163,302.91 C269.968163,294.75 259.148163,290.21 247.518163,290.13 L247.178163,290.13 C234.348163,290.13 218.958163,295.6 205.998163,304.76 C192.248163,314.48 184.008163,326.2 183.388163,336.91 C182.948163,344.67 186.458163,352.73 193.548163,360.24 C204.938163,372.29 223.378163,380.39 239.438163,380.39 M150.528163,412.56 C154.508163,412.56 158.588163,411.14 162.518163,409.76 C164.678163,409 167.298163,408.09 168.978163,407.84 C170.228163,408.21 171.668163,408.71 173.178163,409.23 C177.438163,410.71 182.268163,412.38 186.748163,412.38 C191.988163,412.38 195.098163,410.11 196.788163,408.2 C199.808163,404.8 200.878163,399.85 200.068163,393.07 C198.578163,380.68 190.928163,369.83 183.518163,359.34 C180.408163,354.93 177.468163,350.76 174.998163,346.55 C173.748163,344.41 171.458163,343.09 168.978163,343.08 C168.968163,343.08 168.968163,343.08 168.958163,343.08 C166.488163,343.08 164.198163,344.38 162.938163,346.51 C160.448163,350.7 157.438163,354.86 154.258163,359.27 C146.688163,369.74 138.858163,380.57 137.628163,392.94 C137.178163,397.52 137.038163,404.14 141.098163,408.62 C142.728163,410.43 145.678163,412.56 150.528163,412.56 M319.388163,188.03 C319.078163,188.03 318.758163,188.03 318.438163,188.04 C296.208163,188.37 273.968163,188.46 251.738163,188.46 C224.258163,188.46 196.778163,188.32 169.298163,188.32 C163.788163,188.33 158.268163,188.33 152.758163,188.33 C136.888163,188.33 121.028163,188.31 105.158163,188.31 C76.1681627,188.31 47.1781627,188.37 18.1881627,188.72 C12.1981627,188.79 1.37816269,193.11 1.10816269,196.18 C-0.531837312,214.99 0.0181626877,234.01 0.438162688,252.94 C0.488162688,255.28 4.24816269,257.46 6.09816269,259.86 C10.6981627,265.8 18.3581627,271.46 19.1081627,277.89 C21.1281627,295.17 22.3681627,313.1 19.9581627,330.22 C13.8981627,373.25 35.5181627,404.07 78.5281627,408.4 C92.6881627,409.83 100.468163,413.43 98.6381627,429.55 C96.3381627,449.81 106.938163,463.97 126.298163,465.57 C140.318163,466.73 154.458163,467.3 168.598163,467.3 C182.998163,467.3 197.408163,466.71 211.698163,465.56 C230.128163,464.07 240.708163,450.39 238.828163,431.04 C237.248163,414.75 243.738163,409.87 259.428163,408.33 C302.238163,404.13 323.658163,372.54 317.548163,329.82 C314.928163,311.5 317.788163,292.34 318.878163,273.6 C319.118163,269.47 321.948163,262.3 324.298163,261.99 C341.788163,259.69 336.688163,246.66 337.178163,236.55 C337.658163,226.76 336.518163,216.86 337.448163,207.13 C338.798163,192.79 333.308163,188.03 319.388163,188.03 L319.388163,188.03 Z M97.2381627,373.47 C96.9581627,373.47 96.6881627,373.47 96.4181627,373.46 C73.9881627,373.04 53.9481627,353.56 54.3381627,332.57 C54.7081627,312.79 70.8481627,296.88 90.3481627,296.88 C90.5881627,296.88 90.8281627,296.88 91.0781627,296.89 C116.158163,297.4 146.798163,320.09 146.988978,338.3 C147.158163,354.89 120.708163,373.47 97.2381627,373.47 L97.2381627,373.47 Z M239.438163,373.39 C215.858163,373.39 189.428163,354.03 190.388163,337.3 C191.418163,319.31 222.848163,297.12 247.188163,297.12 C247.288163,297.12 247.378163,297.12 247.478163,297.12 C267.668163,297.27 283.438163,313.36 283.208163,333.59 C282.968163,354.66 262.958163,373.09 239.998163,373.39 C239.808163,373.39 239.618163,373.39 239.438163,373.39 L239.438163,373.39 Z M150.528163,405.56 C146.378163,405.56 143.678163,402.91 144.598163,393.64 C146.108163,378.47 160.388163,364.56 168.968163,350.09 C177.468163,364.64 191.278163,378.56 193.128163,393.91 C194.198163,402.79 191.208163,405.38 186.758163,405.38 C181.688163,405.38 174.728163,402.02 169.668163,400.77 C164.178163,400.82 156.068163,405.56 150.528163,405.56 L150.528163,405.56 Z" id="Shape"></path>
                <path d="M72.2381627,7 C77.6681627,7 82.4381627,8.46 85.6681627,11.11 C88.8581627,13.73 90.5281627,17.43 90.6281627,22.1 C91.3981627,56.96 91.2581627,93.89 91.1281627,129.6 C91.0881627,140.85 91.0481627,152.43 91.0281627,163.85 L53.3581627,163.85 C53.3481627,152.51 53.3081627,141 53.2681627,129.81 C53.1481627,94.16 53.0181627,57.29 53.7481627,22.44 C54.0481627,8.19 67.8581627,7.02 72.0881627,7 L72.2381627,7 M72.2381627,-1.42108547e-14 C72.1781627,-1.42108547e-14 72.1281627,-1.42108547e-14 72.0681627,-1.42108547e-14 C57.8181627,0.06 47.0581627,8.15 46.7681627,22.29 C45.7681627,70.51 46.3881627,121.59 46.3881627,170.85 C63.9681627,170.85 79.4481627,170.85 98.0481627,170.85 C98.0481627,121.33 98.6981691,70.2 97.6381627,21.95 C97.3181627,7.88 86.3481627,-1.42108547e-14 72.2381627,-1.42108547e-14 L72.2381627,-1.42108547e-14 Z" id="Shape"></path>
                <path d="M265.138163,7.17 L265.538163,7.17 C277.748163,7.32 283.998163,13.85 284.118163,26.58 C284.438163,59.86 284.378163,95.2 284.318163,129.37 C284.298163,140.49 284.278163,151.92 284.278163,163.23 L246.308163,163.23 C246.298163,151.97 246.268163,140.53 246.228163,129.4 C246.128163,94.38 246.018163,58.17 246.598163,23.9 C246.858163,8.8 259.648163,7.17 265.138163,7.17 M265.138163,0.17 C250.338163,0.17 239.858163,8.66 239.608163,23.79 C238.798163,71.37 239.308163,121.82 239.308163,170.24 C258.418163,170.24 274.318163,170.24 291.268163,170.24 C291.268163,122.15 291.558163,72.91 291.108163,26.52 C290.958163,10.5 281.998163,0.38 265.618163,0.18 C265.458163,0.17 265.298163,0.17 265.138163,0.17 L265.138163,0.17 Z" id="Shape"></path>
            </g>
        </g>
    </svg>
  `,
  remove: `
    <svg viewBox="0 0 64 64">
      <path d="M0.309,0.309a0.9,0.9,0,0,1,1.268,0L63.691,62.423a0.9,0.9,0,0,1-1.268,1.268L0.309,1.577A0.9,0.9,0,0,1,.309.309Z"/>
      <path d="M63.691,0.309a0.9,0.9,0,0,1,0,1.268L1.577,63.691A0.9,0.9,0,0,1,.309,62.423L62.423,0.309A0.9,0.9,0,0,1,63.691.309Z"/>
    </svg>
  `,
  share: `
    <svg viewBox="0 0 24 24">
      <path d="M20 13v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6M12 15V3m0 0L8.5 6.5M12 3l3.5 3.5" fill="none" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>
  `,
  buy: `
    <svg viewBox="0 0 24 24">
      <path d="M19.26 9.696l1.385 9A2 2 0 0118.67 21H5.33a2 2 0 01-1.977-2.304l1.385-9A2 2 0 016.716 8h10.568a2 2 0 011.977 1.696zM14 5a2 2 0 10-4 0" fill="none" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>
  `,
  spinner: `
    <svg class="wk-spinner-svg" viewBox="0 0 66 66">
      <circle class="wk-spinner-circle" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
    </svg>
  `,
});
